import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    AlertCircle, AlertTriangle, Ban, CheckCircle2, ChevronLeft, ChevronRight, Download, Edit, Eye,
    Loader2, Plus, Printer, Receipt, RefreshCw, Search, Trash2, X,
} from 'lucide-react';
import styles from './InvoiceHistory.module.css';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import GlobalDateFilter from '../../components/GlobalDateFilter/GlobalDateFilter';
import {
    differsFrom, formatDate as fmtDay, formatTime as fmtTime, getBillDate, getCompletedAt,
    getCreatedAt, getUpdatedAt, isBillInRange, toLocalYMD,
} from '../../utils/billDates';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001')
    .replace(/\/api\/v1(?:\/public)?\/?$/, '')
    .replace(/\/+$/, '');
const INVOICES_API = `${API_ORIGIN}/api/v1/smiify/sm/invoices`;

const formatMoney = (value) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);

// Why a completed bill is cancelled (the server keeps it with the bill)
const CANCEL_REASONS = ['Wrong client', 'Wrong items or price', 'Duplicate bill', 'Client did not pay', 'Test bill', 'Other'];

// Last 30 days including today (works across month / year change)
const DEFAULT_RANGE_DAYS = 30;

const getDaysAgoDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return toLocalYMD(d);
};

const getTodayDate = () => toLocalYMD(new Date());

const getDefaultDateRange = () => ({
    startDate: getDaysAgoDate(DEFAULT_RANGE_DAYS - 1),
    endDate: getTodayDate(),
    label: 'Last 30 Days',
});

/* ---------- confirm box for cancelling a completed bill ---------- */
const CancelBillModal = ({ invoice, busy, error, onClose, onConfirm }) => {
    const [reason, setReason] = useState('');
    const [note, setNote] = useState('');
    const usesLoyalty = (invoice.items || []).some((i) => ['package', 'prepaid', 'discount', 'membership'].includes(String(i.itemType).toLowerCase()) || i.packageRedeem?.packageId);
    const p = invoice.payments || {};
    const fromAccount = (Number(p.wallet) || 0) + (Number(p.prepaidCardAmount) || 0);
    const ready = reason && (reason !== 'Other' || note.trim());

    return (
        <div role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
            style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'grid', placeItems: 'center', padding: 16, background: 'rgba(24, 24, 26, 0.45)', fontFamily: 'var(--font-family-base, Nunito, sans-serif)' }}>
            <div role="dialog" aria-modal="true" aria-label="Cancel bill" style={{ width: 'min(520px, 100%)', maxHeight: '90vh', overflowY: 'auto', borderRadius: 16, background: '#fff', boxShadow: '0 24px 60px -20px rgba(24,24,26,.45)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '16px 18px', borderBottom: '1px solid var(--h-color-neutral-150, #e9e9ec)' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>Cancel bill {invoice.invoiceNo}</h3>
                        <p style={{ margin: '3px 0 0', fontSize: 12, fontWeight: 700, color: 'var(--h-color-neutral-600, #58585e)' }}>
                            {invoice.customer?.name || 'Walk-in'} · {formatMoney(invoice.summary?.grandTotal)}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} disabled={busy} aria-label="Close" style={{ width: 30, height: 30, border: 0, borderRadius: 8, background: 'var(--h-color-neutral-50, #f8f9fa)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={16} /></button>
                </div>

                <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--h-color-neutral-600, #58585e)', marginBottom: 8 }}>Why is it being cancelled?</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="radiogroup" aria-label="Reason">
                            {CANCEL_REASONS.map((r) => (
                                <button type="button" key={r} role="radio" aria-checked={reason === r} onClick={() => setReason(r)} disabled={busy}
                                    style={{ height: 32, padding: '0 12px', borderRadius: 999, fontFamily: 'inherit', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                                        border: `1px solid ${reason === r ? 'var(--h-color-danger-600, #be1025)' : 'var(--h-color-neutral-150, #e9e9ec)'}`,
                                        background: reason === r ? 'var(--h-color-danger-50, #fff4f3)' : '#fff', color: reason === r ? 'var(--h-color-danger-600, #be1025)' : 'var(--h-color-neutral-800, #2d2d31)' }}>
                                    {r}
                                </button>
                            ))}
                        </div>
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={2} disabled={busy}
                            placeholder={reason === 'Other' ? 'Write the reason (needed)' : 'Note (optional)'}
                            aria-label="Note"
                            style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--h-color-neutral-150, #e9e9ec)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 650, resize: 'vertical' }} />
                    </div>

                    <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--h-color-warning-100, #ffeec8)', color: 'var(--h-color-warning-700, #9b4600)', fontSize: 12, fontWeight: 700, lineHeight: 1.5 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 900, marginBottom: 4 }}><AlertTriangle size={14} /> What happens</div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            <li>The bill stays in history as Cancelled. Its number is not used again.</li>
                            {invoice.customer?.customerId && <li>{fromAccount > 0 ? `${formatMoney(fromAccount)} from khata / prepaid card goes back to the client. ` : ''}Any due or advance this bill made is undone.</li>}
                            {usesLoyalty && <li>Packages, memberships or cards bought on it are removed, and package services used on it are given back. If one is already used, the bill cannot be cancelled.</li>}
                            {invoice.customer?.customerId && <li>The client&apos;s total spend and visits go down.</li>}
                            <li>Sales reports, day closing and staff incentive stop counting it. Cash or UPI already taken is not returned by this; give it back by hand if needed.</li>
                        </ul>
                    </div>

                    {error && <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--h-color-danger-50, #fff4f3)', color: 'var(--h-color-danger-600, #be1025)', fontSize: 12, fontWeight: 800 }}><AlertCircle size={14} /> {error}</div>}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--h-color-neutral-150, #e9e9ec)' }}>
                    <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>Keep the bill</button>
                    <button type="button" onClick={() => onConfirm(reason, note.trim())} disabled={!ready || busy}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px', border: 0, borderRadius: 9, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 900, color: '#fff', cursor: ready && !busy ? 'pointer' : 'not-allowed', opacity: ready && !busy ? 1 : 0.55, background: 'var(--h-color-danger-600, #be1025)' }}>
                        {busy ? <Loader2 size={14} className={styles.spin} /> : <Ban size={14} />} Cancel bill
                    </button>
                </div>
            </div>
        </div>
    );
};

const InvoiceHistory = () => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { hasPermission } = useAuth();
    const storeId = activeStore?._id || activeStore?.id;

    // Live access (the server checks the same rules)
    const canCreate = hasPermission('billing', 'create');
    const canExport = hasPermission('billing', 'export_history');
    const canEditDraft = hasPermission('billing', 'edit_draft') || canCreate;
    const canEditCompleted = hasPermission('billing', 'edit');
    const canPrint = hasPermission('billing', 'print_bill');
    const canCancelDraft = hasPermission('billing', 'cancel_draft');
    const canCancelCompleted = hasPermission('billing', 'cancel_invoice');
    const token = localStorage.getItem('smiify_token');

    const [invoices, setInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [cancellingId, setCancellingId] = useState('');
    const [cancelTarget, setCancelTarget] = useState(null);   // completed bill being cancelled
    const [cancelError, setCancelError] = useState('');
    const [toast, setToast] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateRange, setDateRange] = useState(getDefaultDateRange);

    const [page, setPage] = useState(1);
    const pageSize = 50;

    /* ------------------------------ Fetch ------------------------------ */
    const fetchInvoices = useCallback(async () => {
        if (!token) {
            setIsLoading(false);
            setLoadError('You are logged out. Please log in again.');
            return;
        }
        if (!storeId || storeId === 'global_hq') {
            setIsLoading(false);
            setLoadError('Select a branch to see its invoices.');
            return;
        }

        setIsLoading(true);
        setLoadError('');
        try {
            const response = await fetch(INVOICES_API, {
                headers: { Authorization: `Bearer ${token}`, 'x-store-id': storeId },
            });
            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.success === false) {
                setInvoices([]);
                setLoadError(result.message || `Could not load invoices (error ${response.status}).`);
                return;
            }
            setInvoices(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            console.error('Failed to fetch invoices', error);
            setInvoices([]);
            setLoadError('Cannot reach the server. Check that the backend is running.');
        } finally {
            setIsLoading(false);
        }
    }, [storeId, token]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    /* ------------------------------ Filters ------------------------------ */
    const filteredInvoices = useMemo(() => {
        let result = [...invoices];

        // Completed bills count on the day they were completed; drafts on the day they were started
        if (dateRange.startDate && dateRange.endDate) {
            result = result.filter((inv) => isBillInRange(inv, dateRange.startDate, dateRange.endDate));
        }

        if (statusFilter !== 'all') {
            result = result.filter((inv) => String(inv.status || '').toLowerCase() === statusFilter);
        }

        const query = searchQuery.trim().toLowerCase();
        if (query) {
            result = result.filter((inv) =>
                String(inv.invoiceNo || '').toLowerCase().includes(query) ||
                String(inv.customer?.name || '').toLowerCase().includes(query) ||
                String(inv.customer?.phone || '').includes(query)
            );
        }

        return result.sort((a, b) => getBillDate(b) - getBillDate(a));
    }, [invoices, searchQuery, statusFilter, dateRange]);

    const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const visibleInvoices = filteredInvoices.slice((safePage - 1) * pageSize, safePage * pageSize);
    const hiddenByDate = invoices.length > 0 && filteredInvoices.length === 0 && !searchQuery && statusFilter === 'all';

    /* ------------------------------ Actions ------------------------------ */
    const handleExportExcel = () => {
        if (filteredInvoices.length === 0) return alert('No data to export!');

        const exportData = filteredInvoices.map((inv) => ({
            'Invoice No': inv.invoiceNo || 'DRAFT',
            'Completed On': getCompletedAt(inv) ? `${fmtDay(getCompletedAt(inv))} ${fmtTime(getCompletedAt(inv))}` : '',
            'Created On': `${fmtDay(getCreatedAt(inv))} ${fmtTime(getCreatedAt(inv))}`,
            'Last Updated': `${fmtDay(getUpdatedAt(inv))} ${fmtTime(getUpdatedAt(inv))}`,
            'Customer Name': inv.customer?.name || 'Walk-in',
            'Customer Phone': inv.customer?.phone || 'N/A',
            'Total Items': inv.items?.length || 0,
            'Net Taxable': inv.summary?.sumTaxable || 0,
            'Total GST': inv.summary?.sumTax || 0,
            'Net Payable': inv.summary?.grandTotal || 0,
            Status: String(inv.status || 'draft').toUpperCase(),
            'Cancel Reason': inv.cancelReason || '',
            'Cancelled On': inv.cancelledAt ? `${fmtDay(inv.cancelledAt)} ${fmtTime(inv.cancelledAt)}` : '',
            'Cancelled By': inv.cancelledByName || '',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Invoice_History');
        XLSX.writeFile(wb, `Invoice_History_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
    };

    const markCancelled = (id, data) => setInvoices((list) => list.map((item) => (item._id === id ? { ...item, ...(data || {}), status: 'cancelled' } : item)));

    const handleCancelDraft = async (inv) => {
        if (!window.confirm(`Cancel draft ${inv.invoiceNo || ''}? This cannot be undone.`)) return;
        setCancellingId(inv._id);
        try {
            const response = await fetch(`${INVOICES_API}/${inv._id}/cancel`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.success === false) {
                alert(result.message || 'Could not cancel this bill.');
                return;
            }
            markCancelled(inv._id, result.data);
        } catch (error) {
            console.error('Cancel failed', error);
            alert('Cannot reach the server.');
        } finally {
            setCancellingId('');
        }
    };

    // completed bill: the server undoes its khata / card / package / membership effects, then cancels it
    const confirmCancelCompleted = async (reason, note) => {
        if (!cancelTarget) return;
        setCancellingId(cancelTarget._id);
        setCancelError('');
        try {
            const response = await fetch(`${INVOICES_API}/${cancelTarget._id}/cancel`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
                body: JSON.stringify({ reason, note }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.success === false) {
                setCancelError(result.message || `Could not cancel this bill (error ${response.status}).`);
                return;
            }
            markCancelled(cancelTarget._id, result.data);
            setCancelTarget(null);
            setToast(result.message || 'Bill cancelled.');
            setTimeout(() => setToast(''), 6000);
        } catch (error) {
            setCancelError('Cannot reach the server.');
        } finally {
            setCancellingId('');
        }
    };

    const openPreview = (id, print = false) => navigate(`/create-bill/${id}/preview${print ? '?print=1' : ''}`);

    /* ------------------------------ Render ------------------------------ */
    if (isLoading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--h-color-neutral-50)' }}>
                <Loader2 className={styles.spin} size={28} color="var(--h-color-primary-600)" />
                <p style={{ marginTop: '12px', fontSize: '11.5px', color: 'var(--h-color-neutral-500)', fontWeight: 700 }}>Loading invoice history...</p>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* === ACTION TOOLBAR === */}
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    <div className={styles.searchBox}>
                        <Search size={15} color="var(--h-color-neutral-400)" />
                        <input
                            type="text"
                            placeholder="Search invoice #, name or phone..."
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                        />
                    </div>

                    <select className={styles.filterSelect} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                        <option value="all">All statuses</option>
                        <option value="completed">Completed</option>
                        <option value="draft">Drafts</option>
                        <option value="cancelled">Cancelled</option>
                    </select>

                    <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--h-color-neutral-500)', marginLeft: '10px' }}>
                        Showing {filteredInvoices.length} of {invoices.length} invoices
                    </div>
                </div>

                <div className={styles.toolbarRight}>
                    <GlobalDateFilter
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        defaultLabel={dateRange.label}
                        onChange={(range) => { setDateRange(range); setPage(1); }}
                    />
                    <button className={styles.secondaryBtn} onClick={fetchInvoices} title="Reload">
                        <RefreshCw size={14} />
                    </button>
                    {canExport && (
                        <button className={styles.secondaryBtn} onClick={handleExportExcel}>
                            <Download size={14} /> Export
                        </button>
                    )}
                    {canCreate && (
                        <button className={styles.primaryBtn} onClick={() => navigate('/create-bill')}>
                            <Plus size={15} /> Create Bill
                        </button>
                    )}
                </div>
            </div>

            {toast && (
                <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 16px 10px', padding: '10px 12px', borderRadius: 10, background: 'var(--h-color-success-100, #dcf8e3)', color: 'var(--h-color-success-600, #007a49)', fontSize: 12, fontWeight: 800 }}>
                    <CheckCircle2 size={14} /> <span>{toast}</span>
                </div>
            )}

            {/* === DATA TABLE === */}
            <main className={styles.contentArea}>
                <div className={styles.tableContainer}>
                    <div className={styles.tableWrapper}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Invoice No.</th>
                                    <th>Customer Details</th>
                                    <th style={{ textAlign: 'center' }}>Items</th>
                                    <th style={{ textAlign: 'right' }}>Net Taxable</th>
                                    <th style={{ textAlign: 'right' }}>Total GST</th>
                                    <th style={{ textAlign: 'right' }}>Net Payable</th>
                                    <th style={{ textAlign: 'center' }}>Status</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadError ? (
                                    <tr>
                                        <td colSpan="9">
                                            <div className={styles.emptyState}>
                                                <AlertCircle size={36} color="var(--h-color-danger-500)" style={{ marginBottom: '14px' }} />
                                                <h3>Invoices could not be loaded</h3>
                                                <p>{loadError}</p>
                                                <button className={styles.secondaryBtn} style={{ marginTop: '14px' }} onClick={fetchInvoices}>
                                                    <RefreshCw size={14} /> Try again
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : visibleInvoices.length === 0 ? (
                                    <tr>
                                        <td colSpan="9">
                                            <div className={styles.emptyState}>
                                                <Receipt size={36} color="var(--h-color-neutral-300)" style={{ marginBottom: '14px' }} />
                                                <h3>No invoices found</h3>
                                                <p>
                                                    {hiddenByDate
                                                        ? `${invoices.length} invoices exist, but none in ${dateRange.label || 'this date range'}. Change the date filter.`
                                                        : 'Try adjusting your search or date range to find the bill.'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    visibleInvoices.map((inv) => {
                                        const statusStr = String(inv.status || 'draft').toLowerCase();
                                        const completedAt = getCompletedAt(inv);
                                        const createdAt = getCreatedAt(inv);
                                        const updatedAt = getUpdatedAt(inv);
                                        let statusClass = styles.statusDraft;
                                        if (statusStr === 'completed') statusClass = styles.statusComplete;
                                        if (statusStr === 'cancelled') statusClass = styles.statusCancelled;
                                        const cancelInfo = statusStr === 'cancelled' && (inv.cancelReason || inv.cancelledAt)
                                            ? `${inv.cancelReason || 'Cancelled'}${inv.cancelledByName ? `, by ${inv.cancelledByName}` : ''}${inv.cancelledAt ? `, ${fmtDay(inv.cancelledAt)} ${fmtTime(inv.cancelledAt)}` : ''}`
                                            : '';

                                        return (
                                            <tr key={inv._id} style={statusStr === 'cancelled' ? { opacity: 0.72 } : undefined}>
                                                <td>
                                                    <div className={styles.dateWrapper}>
                                                        {completedAt && (
                                                            <div className={styles.dateBlock}>
                                                                <span className={styles.dateLabel}>Completed</span>
                                                                <span className={styles.dateValue}>{fmtDay(completedAt)} <span className={styles.timeValue}>{fmtTime(completedAt)}</span></span>
                                                            </div>
                                                        )}
                                                        <div className={styles.dateBlock} style={completedAt ? { opacity: 0.75 } : undefined}>
                                                            <span className={styles.dateLabel}>Created</span>
                                                            <span className={styles.dateValue}>{fmtDay(createdAt)} <span className={styles.timeValue}>{fmtTime(createdAt)}</span></span>
                                                        </div>
                                                        {updatedAt && differsFrom(updatedAt, completedAt || createdAt) && (
                                                            <div className={styles.dateBlock} style={{ opacity: 0.75 }}>
                                                                <span className={styles.dateLabel}>Updated</span>
                                                                <span className={styles.dateValue}>{fmtDay(updatedAt)} <span className={styles.timeValue}>{fmtTime(updatedAt)}</span></span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    <span className={styles.invoiceNoCell} onClick={() => openPreview(inv._id)} title="Click to view full bill" style={statusStr === 'cancelled' ? { textDecoration: 'line-through' } : undefined}>
                                                        {inv.invoiceNo || 'DRAFT'}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div className={styles.clientCell}>
                                                        <span className={styles.clientName}>{inv.customer?.name || 'Walk-in Customer'}</span>
                                                        <span className={styles.clientPhone}>{inv.customer?.phone ? `+91 ${inv.customer.phone}` : '—'}</span>
                                                    </div>
                                                </td>

                                                <td style={{ textAlign: 'center', color: 'var(--h-color-neutral-600)' }}>{inv.items?.length || 0}</td>

                                                <td style={{ textAlign: 'right' }}>
                                                    <span className={styles.moneyCell}>{formatMoney(inv.summary?.sumTaxable)}</span>
                                                </td>

                                                <td style={{ textAlign: 'right' }}>
                                                    <span className={styles.gstCell}>{formatMoney(inv.summary?.sumTax)}</span>
                                                </td>

                                                <td style={{ textAlign: 'right' }}>
                                                    <span className={styles.moneyCell} style={{ color: statusStr === 'cancelled' ? 'inherit' : 'var(--h-color-success-600)' }}>
                                                        {formatMoney(inv.summary?.grandTotal)}
                                                    </span>
                                                </td>

                                                <td style={{ textAlign: 'center' }}>
                                                    <span className={`${styles.statusTag} ${statusClass}`} title={cancelInfo || undefined}>{statusStr}</span>
                                                    {cancelInfo && (
                                                        <div title={cancelInfo} style={{ marginTop: 4, maxWidth: 150, marginInline: 'auto', fontSize: 10, fontWeight: 700, color: 'var(--h-color-neutral-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {inv.cancelReason || 'Cancelled'}
                                                        </div>
                                                    )}
                                                </td>

                                                <td>
                                                    <div className={styles.actionCell}>
                                                        {statusStr !== 'cancelled' && (statusStr === 'draft' ? canEditDraft : canEditCompleted) && (
                                                            <button
                                                                className={styles.iconBtn}
                                                                title={statusStr === 'draft' ? 'Continue draft' : 'Update invoice'}
                                                                onClick={() => navigate(`/update-invoice/${inv._id}`)}
                                                            >
                                                                <Edit size={14} />
                                                            </button>
                                                        )}

                                                        <button className={styles.iconBtn} title="View bill" onClick={() => openPreview(inv._id)}>
                                                            <Eye size={14} />
                                                        </button>

                                                        {statusStr !== 'cancelled' && canPrint && (
                                                            <button className={styles.iconBtn} title="Print bill" onClick={() => openPreview(inv._id, true)}>
                                                                <Printer size={14} />
                                                            </button>
                                                        )}

                                                        {statusStr === 'draft' && canCancelDraft && (
                                                            <button
                                                                className={`${styles.iconBtn} ${styles.delete}`}
                                                                title="Cancel draft"
                                                                onClick={() => handleCancelDraft(inv)}
                                                                disabled={cancellingId === inv._id}
                                                            >
                                                                {cancellingId === inv._id ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />}
                                                            </button>
                                                        )}

                                                        {/* Completed bill: undo everything it did, then keep it as Cancelled */}
                                                        {statusStr === 'completed' && canCancelCompleted && inv.source !== 'MEMBERSHIP_DESK' && (
                                                            <button
                                                                className={`${styles.iconBtn} ${styles.delete}`}
                                                                title="Cancel bill (wrong bill)"
                                                                aria-label={`Cancel bill ${inv.invoiceNo}`}
                                                                onClick={() => { setCancelError(''); setCancelTarget(inv); }}
                                                                disabled={cancellingId === inv._id}
                                                            >
                                                                <Ban size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.tablePager}>
                        <span>
                            Showing <strong>{visibleInvoices.length > 0 ? (safePage - 1) * pageSize + 1 : 0}–{Math.min(safePage * pageSize, filteredInvoices.length)}</strong> of <strong>{filteredInvoices.length}</strong>
                        </span>
                        <div className={styles.pageButtons}>
                            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>
                                <ChevronLeft size={14} />
                            </button>
                            <span>Page {safePage} / {totalPages}</span>
                            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            {cancelTarget && (
                <CancelBillModal
                    invoice={cancelTarget}
                    busy={cancellingId === cancelTarget._id}
                    error={cancelError}
                    onClose={() => setCancelTarget(null)}
                    onConfirm={confirmCancelCompleted}
                />
            )}
        </div>
    );
};

export default InvoiceHistory;
